import * as React from 'react'

import { Box, Icon, Table, colors } from '@factorialco/deprecated-design-system'

import TextField from 'modules/core/components/TextField'
import i18n from 'modules/core/lib/i18n'

import { CostCenterOption } from 'modules/cost_centers/components/CostCenterAssigments/components/AssignmentsManager/Picker/hooks/useCostCenterByLegalEntities'
import { DEFAULT_ASSIGNMENT_PERCENTAGE } from 'modules/cost_centers/components/CostCenterAssigments/lib/defaults'
import { FormattedAssignment } from 'modules/cost_centers/components/CostCenterAssigments/lib/types'

import CostCenterStructuredPicker from './CostCenterStructuredPicker'
import './index.styles.css'

interface Props {
  assignment: FormattedAssignment
  selectedCostCenterIds: string[]
  legalEntityIds: string[]
  readonly?: boolean
  onDelete: (assignmentId: string) => void
  onCostCenterChange: (assignmentId: string, option: CostCenterOption | null) => void
  onPercentageChange: (assignmentId: string, percentage: number) => void
  employeeId?: string
}

const StructuredValueRow = ({
  assignment,
  selectedCostCenterIds,
  legalEntityIds,
  readonly,
  onDelete,
  onCostCenterChange,
  onPercentageChange,
  employeeId,
}: Props) => {
  const [hover, setHover] = React.useState<boolean>(false)
  const hasNotPercentage = assignment.percentage === DEFAULT_ASSIGNMENT_PERCENTAGE
  const notValid = !hasNotPercentage && (assignment.percentage <= 0 || assignment.percentage > 1)

  // When assignment is default, disable the cost center selectors (only % editable)
  const selectorsReadonly = readonly || assignment.isDefault

  // Build the current cost center value for display
  const costCenterValue = assignment.costCenterId
    ? {
        id: String(assignment.costCenterId),
        name: assignment.name || '',
      }
    : undefined

  const handleCostCenterChange = (option: CostCenterOption) => {
    onCostCenterChange(assignment.id, option)
  }

  const handlePercentageChange = (newValue: string) => {
    const percentage = newValue === '' ? DEFAULT_ASSIGNMENT_PERCENTAGE : Number(newValue) / 100
    onPercentageChange(assignment.id, percentage)
  }

  return (
    <Table.Row>
      {/* Cost Center Column */}
      <Table.Cell
        padding="s0"
        wide
        style={{
          background: selectorsReadonly ? colors.grey200 : 'transparent',
        }}
      >
        <Box className={'StructuredPicker_borderlessSelector'}>
          <CostCenterStructuredPicker
            value={costCenterValue}
            selectedIds={selectedCostCenterIds}
            legalEntityIds={legalEntityIds}
            onChange={handleCostCenterChange}
            readonly={selectorsReadonly}
            employeeId={employeeId}
          />
        </Box>
      </Table.Cell>

      {/* Percentage Column */}
      <Table.Cell
        padding="s0"
        width={120}
        style={{
          background: readonly ? colors.grey200 : 'transparent',
        }}
      >
        <Box
          className={
            `${'StructuredPicker_borderlessInput'}` +
            `${notValid ? ` ${'StructuredPicker_errorBorderlessInput'}` : ''}`
          }
          justifyContent="center"
          height="full"
          border={{
            left: {
              width: 's1',
              style: 'solid',
              color: 'darker160',
            },
          }}
        >
          <TextField
            {...(!hasNotPercentage && { defaultValue: (assignment.percentage * 100).toFixed(2) })}
            onChange={handlePercentageChange}
            type="number"
            min={0.0}
            readonly={readonly}
            step={'5'}
            placeholder={i18n.t(
              'finance.employee_dimensions_info.modals.set_assignments.default_value'
            )}
            isNotValid={notValid}
            onKeyDown={(e) => {
              if (e.key === '-') {
                e.preventDefault()
              }
            }}
            hideError
          />
        </Box>
      </Table.Cell>

      {/* Delete Button Column */}
      <Table.Cell width={40} padding="s0">
        <Box
          paddingX="s8"
          paddingY="s12"
          background={hover && !selectorsReadonly ? 'grey300' : 'grey100'}
          alignItems="center"
          cursor={selectorsReadonly ? 'default' : 'pointer'}
          border={{
            left: {
              width: 's1',
              color: 'grey400',
              style: 'solid',
            },
          }}
          onMouseEnter={() => !selectorsReadonly && setHover(true)}
          onMouseLeave={() => !selectorsReadonly && setHover(false)}
          onClick={() => !selectorsReadonly && onDelete(assignment.id)}
        >
          <Icon.Medium icon="delete" color={selectorsReadonly ? 'grey400' : undefined} />
        </Box>
      </Table.Cell>
    </Table.Row>
  )
}

export default StructuredValueRow
